import React, { useState, useRef, useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import { useForm } from 'react-hook-form';

// store
import { useStoreValue, storeSet } from '../../../../store';

// components
import ButtonFlat from '../../../../components/ButtonWrapper/components/ButtonFlat';
import ButtonFormSubmit from '../../../../components/ButtonWrapper/components/ButtonFormSubmit';
import FormField from '../../../../components/FormField';
import UserAvatarPlaceholder from '../../../../components/UserAvatarPlaceholder';
import FileuploadIcon from '../../../../icons/FileuploadIcon';
import CrownIcon from '../../../../icons/CrownIcon';

const ProfileAccount = ({ setActiveSection }) => {
    const userData = useStoreValue('userData');

    const [avatarPreview, setAvatarPreview] = useState(userData?.profile_picture_url ?? null);
    const [avatarFile, setAvatarFile] = useState(null);
    const fileInputRef = useRef(null);

    const { control, handleSubmit, reset } = useForm({
        mode: 'onChange',
        defaultValues: {
            name:         userData?.name ?? '',
            surname:      userData?.surname ?? '',
            email:        userData?.email ?? '',
            phone_number: userData?.phone_number ?? '',
            location:     userData?.location ?? '',
            website:      userData?.website ?? '',
            profession:   userData?.profession ?? '',
            bio:          userData?.bio ?? '',
        },
    });

    useEffect(() => {
        if (!userData?.id) return;
        reset({
            name:         userData.name ?? '',
            surname:      userData.surname ?? '',
            email:        userData.email ?? '',
            phone_number: userData.phone_number ?? '',
            location:     userData.location ?? '',
            website:      userData.website ?? '',
            profession:   userData.profession ?? '',
            bio:          userData.bio ?? '',
        });
        setAvatarPreview(userData.profile_picture_url ?? null);
    }, [userData?.id]);

    const handleAvatarClick = () => fileInputRef.current?.click();

    const handleAvatarChange = (e) => {
        const file = e.target.files?.[0];
        if (!file || !file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) return;
        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
    };

    const onSubmit = async (data) => {
        const payload = Object.fromEntries(
            Object.entries(data).filter(([, v]) => v !== '' && v !== null && v !== undefined)
        );
        const formData = new FormData();
        formData.append('Payload', JSON.stringify(payload));
        if (avatarFile) {
            formData.append('profilePicture', avatarFile);
        }
        try {
            await storeSet('updateUser', formData);
        } catch {
            // error already shown by action
        }
    };

    const handleCancel = () => {
        reset();
        setAvatarFile(null);
        setAvatarPreview(userData?.profile_picture_url ?? null);
    };

    return (
        <div className="workbenchProfile workbenchProfile--account">

            {/* Avatar — top left */}
            <section className="workbenchProfile__avatar" aria-labelledby="profile-avatar-heading">
                <h3 className="workbenchProfile__sectionTitle" id="profile-avatar-heading">
                    {__('Foto profilo', 'soundbetween')}
                </h3>
                <div className="profileAvatar">
                    <div className="profileAvatar__preview">
                        {avatarPreview
                            ? <img src={avatarPreview} alt={__('Avatar', 'soundbetween')}
                                   className="profileAvatar__img"/>
                            : (
                                <UserAvatarPlaceholder/>
                            )
                        }
                    </div>
                    <div className="profileAvatar__info">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="profileAvatar__fileInput"
                            onChange={handleAvatarChange}
                            aria-label={__('Carica foto profilo', 'soundbetween')}
                        />
                        <ButtonFlat onClick={handleAvatarClick}>
                            <FileuploadIcon/>
                            {__('Carica foto', 'soundbetween')}
                        </ButtonFlat>
                        <p className="profileAvatar__hint">
                            {__('JPG, PNG o GIF. Massimo 5MB.', 'soundbetween')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Subscription — top right */}
            <section className="workbenchProfile__subscription" aria-labelledby="profile-subscription-heading">
                <h3 className="workbenchProfile__sectionTitle" id="profile-subscription-heading">
                    {__('Piano attuale', 'soundbetween')}
                </h3>
                <div className="profileSubscription">
                    <span className="profileSubscription__plan">{__('Piano FREE', 'soundbetween')}</span>
                    <ButtonFlat onClick={() => setActiveSection('subscription')}>
                        <CrownIcon/>
                        {__('Passa a PRO', 'soundbetween')}
                    </ButtonFlat>
                </div>
            </section>

            {/* Personal data — full width below */}
            <section className="workbenchProfile__personalData" aria-labelledby="profile-personal-heading">
                <h3 className="workbenchProfile__sectionTitle" id="profile-personal-heading">
                    {__('I dati personali', 'soundbetween')}
                </h3>
                <form className="appForm profileForm" onSubmit={handleSubmit(onSubmit)} noValidate>

                    <fieldset className="profileForm__row">
                        <FormField type="text" name="name" label={__('Nome', 'soundbetween')} placeholder="Nome"
                                   control={control}
                                   rules={{ required: __('Il nome è obbligatorio', 'soundbetween') }}/>
                        <FormField type="text" name="surname" label={__('Cognome', 'soundbetween')}
                                   placeholder="Cognome" control={control}/>
                    </fieldset>

                    <fieldset className="profileForm__row">
                        <FormField type="email" name="email" label={__('E-Mail', 'soundbetween')}
                                   placeholder="E-Mail" control={control} rules={{
                            required: __('L\'email è obbligatoria', 'soundbetween'),
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: __('Inserisci un\'email valida', 'soundbetween')
                            }
                        }}/>
                        <FormField type="text" name="phone_number" label={__('Telefono', 'soundbetween')}
                                   placeholder={__('Telefono (Opzionale)', 'soundbetween')} control={control}/>
                    </fieldset>

                    <fieldset className="profileForm__row">
                        <FormField type="text" name="location" label={__('Località', 'soundbetween')}
                                   placeholder={__('Località (Opzionale)', 'soundbetween')} control={control}/>
                        <FormField type="text" name="website" label={__('Sito Web', 'soundbetween')}
                                   placeholder={__('Sito Web (Opzionale)', 'soundbetween')} control={control}/>
                    </fieldset>

                    <fieldset className="profileForm__row profileForm__row--full">
                        <FormField type="text" name="profession" label={__('Professione', 'soundbetween')}
                                   placeholder={__('Professione (Opzionale)', 'soundbetween')} control={control}/>
                    </fieldset>

                    <fieldset className="profileForm__row profileForm__row--full">
                        <FormField type="textarea" name="bio" rows={7} label={__('Biografia', 'soundbetween')}
                                   placeholder="..." control={control}/>
                    </fieldset>

                    <div className="appFormActions profileForm__actions">
                        <ButtonFlat size="md" type="button" onClick={handleCancel}>
                            {__('Annulla', 'soundbetween')}
                        </ButtonFlat>
                        <ButtonFormSubmit size="md">{__('Salva Modifiche', 'soundbetween')}</ButtonFormSubmit>
                    </div>

                </form>
            </section>

        </div>
    );
};

export default ProfileAccount;
